import { DefaultValue, selector, useRecoilValue } from "recoil"

import { brochureState } from "../brochure/brochure-state"
import { proposalLocalState } from "../proposal/proposal-state"
import { averageMonthEachYearWithoutSolarState } from "../savings/genability"

export const getCalculatedUtilityRate = ({
  fallbackRate,
  baseGenabilityUtilityRate,
  genabilityEstimateRate,
  savedAdjustedRate,
  userAdjustedRate,
}: {
  fallbackRate: number
  baseGenabilityUtilityRate?: number | null
  genabilityEstimateRate?: number | null
  savedAdjustedRate?: number | null
  userAdjustedRate?: number | null
}): number => {
  const baseline = baseGenabilityUtilityRate || genabilityEstimateRate || 0

  if (userAdjustedRate !== null && userAdjustedRate !== undefined) {
    if (savedAdjustedRate !== null && savedAdjustedRate !== undefined) {
      return savedAdjustedRate
    }

    return userAdjustedRate + baseline
  }

  return fallbackRate
}

export const avgGenabilityRate = selector({
  key: `avgGenabilityRate`,
  get: ({ get }) => {
    const averageMonthEachYearWithoutSolar = get(
      averageMonthEachYearWithoutSolarState,
    )

    const afterSolarFirstYearRate =
      averageMonthEachYearWithoutSolar[0]?.rate ?? 0

    return Number(afterSolarFirstYearRate.toFixed(2))
  },
})

export const calculatedUtilityRateState = selector<number>({
  key: `calculatedUtilityRate`,
  get: ({ get }) => {
    const {
      userAdjustedUtilityRate: brochureAdjustedRate,
      baseGenabilityUtilityRate,
      dialUtilityRates,
    } = get(brochureState)

    const {
      userAdjustedUtilityRate: localAdjustedRate,
      genabilityUsageEstimate,
    } = get(proposalLocalState)

    const hasPendingAdjustedRate = localAdjustedRate !== brochureAdjustedRate
    const userAdjustedRate = hasPendingAdjustedRate
      ? localAdjustedRate
      : brochureAdjustedRate

    return getCalculatedUtilityRate({
      fallbackRate: get(avgGenabilityRate),
      baseGenabilityUtilityRate,
      genabilityEstimateRate: genabilityUsageEstimate?.items?.[0]?.rateAmount,
      savedAdjustedRate: hasPendingAdjustedRate ? null : dialUtilityRates[0],
      userAdjustedRate,
    })
  },
  set: ({ get, set }, newValue) => {
    if (newValue instanceof DefaultValue) {
      set(proposalLocalState, (prev) => ({
        ...prev,
        userAdjustedUtilityRate: null,
      }))

      return
    }

    const { baseGenabilityUtilityRate } = get(brochureState)
    const { genabilityUsageEstimate } = get(proposalLocalState)

    const baseline =
      baseGenabilityUtilityRate ||
      genabilityUsageEstimate?.items?.[0]?.rateAmount ||
      0

    set(proposalLocalState, (prev) => ({
      ...prev,
      userAdjustedUtilityRate: Number(newValue - baseline),
    }))
  },
})

export const useEffectiveUtilityRate = (): {
  effectiveUtilityRate: number
  hasUserOverride: boolean
  hasBaseline: boolean
} => {
  const utilityRate = useRecoilValue(calculatedUtilityRateState)
  const { userAdjustedUtilityRate, genabilityUsageEstimate } =
    useRecoilValue(proposalLocalState)
  const { baseGenabilityUtilityRate } = useRecoilValue(brochureState)

  const baseline =
    baseGenabilityUtilityRate ||
    genabilityUsageEstimate?.items?.[0]?.rateAmount ||
    0

  const hasUserOverride =
    userAdjustedUtilityRate !== null && userAdjustedUtilityRate !== undefined
  const hasBaseline = baseline > 0

  const getEffectiveRate = (): number => {
    if (hasUserOverride) return utilityRate
    if (hasBaseline) return baseline
    return utilityRate
  }

  return {
    effectiveUtilityRate: getEffectiveRate(),
    hasUserOverride,
    hasBaseline,
  }
}
